import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import GoogleSheetsApiService from '../services/googleSheetsApi';

type MiningSource = 'Strava' | 'Log' | 'Strava & Log';

interface MiningRow {
  date: string;
  wingoCount: number;
  activityLink: string;
  source: MiningSource;
}

/** Parse M/D/YY to timestamp for sorting; newest first. */
function parseRowDate(dateStr: string): number {
  const parts = dateStr.trim().split('/');
  if (parts.length !== 3) return 0;
  const month = parseInt(parts[0], 10) - 1;
  const day = parseInt(parts[1], 10);
  const year = 2000 + parseInt(parts[2], 10);
  return new Date(year, month, day).getTime();
}

/** True if date string (M/D/YY) is in 2025. Used to prefer Log over Strava for initial-run initiation Wingo. */
function is2025(dateStr: string): boolean {
  const parts = dateStr.trim().split('/');
  if (parts.length !== 3) return false;
  const yy = parseInt(parts[2], 10);
  return yy === 25;
}

function sortRowsNewestFirst(rows: MiningRow[]): MiningRow[] {
  return [...rows].sort((a, b) => parseRowDate(b.date) - parseRowDate(a.date));
}

function normalizeUsernameForMatch(value: string): string {
  return value
    .trim()
    .toLowerCase()
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ');
}

const Challenge: React.FC = () => {
  const { username } = useParams<{ username: string }>();
  const [password, setPassword] = useState('');
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [data, setData] = useState<MiningRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [showDetails, setShowDetails] = useState(false);
  const [displayUsername, setDisplayUsername] = useState<string>('');

  useEffect(() => {
    // If user just completed /challenge with the correct password, skip the password gate.
    try {
      const raw = sessionStorage.getItem('wingo_challenge_authed');
      if (raw) {
        const parsed = JSON.parse(raw) as { username?: string };
        if (parsed?.username && username && parsed.username.toLowerCase() === username.toLowerCase()) {
          setIsAuthenticated(true);
        }
      }
    } catch {
      // Ignore parse errors; fall back to password gate.
    }
    // Fetch after auth is set (or already set).
    if (isAuthenticated) {
      fetchData();
    }
  }, [isAuthenticated, username]);

  const handlePasswordSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (password === 'wingo') {
      setIsAuthenticated(true);
    } else {
      setPassword('');
    }
  };

  /** Dev-only: sample rows when API is empty or fails so the page is usable locally. Newest first. */
  const getDevFallbackData = (): MiningRow[] => {
    if (typeof import.meta === 'undefined' || !import.meta.env?.DEV) return [];
    return sortRowsNewestFirst([
      { date: '1/4/26', wingoCount: 12, activityLink: 'https://strava.com/activities/1', source: 'Strava' },
      { date: '1/5/26', wingoCount: 8, activityLink: 'https://strava.com/activities/2', source: 'Strava' },
      { date: '1/6/26', wingoCount: 15, activityLink: 'https://strava.com/activities/3', source: 'Strava' },
    ]);
  };

  /** Extract activity ID from Strava URL for dedupe. */
  const normActivityKey = (link: string): string => {
    if (!link || !link.trim()) return '';
    const m = link.match(/strava\.com\/activities\/(\d+)/i);
    return m ? m[1] : link.trim();
  };

  /** Parse a CSV line handling quoted fields. */
  const parseCSVLine = (line: string): string[] => {
    const values: string[] = [];
    let current = '';
    let inQuotes = false;
    for (let j = 0; j < line.length; j++) {
      const char = line[j];
      if (char === '"') {
        if (inQuotes && line[j + 1] === '"') {
          current += '"';
          j++;
        } else {
          inQuotes = !inQuotes;
        }
      } else if (char === ',' && !inQuotes) {
        values.push(current);
        current = '';
      } else {
        current += char;
      }
    }
    values.push(current);
    return values;
  };

  /** Try to get Mining-only rows from the Ledger Log CSV (source of truth for Mining vs Wager/Gate). */
  const fetchLogMiningOnly = async (): Promise<{ rows: MiningRow[]; displayName: string } | null> => {
    try {
      const sheetsService = GoogleSheetsApiService.getInstance();
      const allLogRows = await sheetsService.getLogData();

      const rows: MiningRow[] = [];
      let displayName = '';

      for (const row of allLogRows) {
        const rowUsername = (row.username || '').trim();
        const category = (row.category || '').trim();
        const wingoMined = Number(row.wingoMined) || 0;

        if (category !== 'Mining') continue;
        if (username && normalizeUsernameForMatch(rowUsername) !== normalizeUsernameForMatch(username)) continue;
        if (wingoMined <= 0) continue;

        if (!displayName) displayName = rowUsername;

        rows.push({
          date: (row.date || '').trim(),
          wingoCount: wingoMined,
          activityLink: (row.activityLink || '').trim(),
          source: 'Log',
        });
      }

      return rows.length ? { rows: sortRowsNewestFirst(rows), displayName } : null;
    } catch {
      return null;
    }
  };

  /** Fetch Mining-only rows from API (Strava segment efforts = mining). */
  const fetchApiMining = async (): Promise<{ rows: MiningRow[]; displayName: string } | null> => {
    try {
      const response = await fetch('/api/export-mining');
      if (!response.ok) return null;
      const csvText = await response.text();
      if (csvText.trim().startsWith('{')) return null;
      const lines = csvText.split('\n').filter((l) => l.trim());
      if (lines.length < 2) return null;

      const headers = parseCSVLine(lines[0]);
      const usernameIndex = headers.indexOf('Username');
      const dateIndex = headers.indexOf('Date');
      const wingoIndex = headers.indexOf('WINGO +/-');
      const activityLinkIndex = headers.indexOf('Activity Link');
      if ([usernameIndex, dateIndex, wingoIndex].some((i) => i === -1)) return null;

      const rows: MiningRow[] = [];
      let displayName = '';
      for (let i = 1; i < lines.length; i++) {
        const values = parseCSVLine(lines[i].trim());
        if (values.length <= Math.max(usernameIndex, dateIndex, wingoIndex)) continue;
        const rowUsername = (values[usernameIndex] || '').trim();
        if (username && normalizeUsernameForMatch(rowUsername) !== normalizeUsernameForMatch(username)) continue;
        const date = (values[dateIndex] || '').trim();
        const wingoCount = parseInt((values[wingoIndex] || '').trim() || '0', 10);
        if (isNaN(wingoCount) || wingoCount <= 0) continue;
        if (!displayName) displayName = rowUsername;
        rows.push({
          date,
          wingoCount,
          activityLink: activityLinkIndex >= 0 ? (values[activityLinkIndex] || '').trim() : '',
          source: 'Strava',
        });
      }
      return rows.length ? { rows: sortRowsNewestFirst(rows), displayName } : null;
    } catch {
      return null;
    }
  };

  /** Merge API + Log mining rows. API is truth except: for 2025, same day + different count → use Log (initiation Wingo doesn't count). No double counting. */
  const mergeMiningRows = (
    apiRows: MiningRow[],
    logRows: MiningRow[]
  ): MiningRow[] => {
    const result: MiningRow[] = apiRows.map((r) => ({ ...r, source: r.source as MiningSource }));
    for (const row of logRows) {
      const apiSameDate = result.find((r) => r.date === row.date);
      const sameSessionFromApi = result.some(
        (r) => r.date === row.date && r.wingoCount === row.wingoCount
      );
      if (sameSessionFromApi) continue;
      // Same date, different count, and 2025: prefer Log (initial-run initiation Wingo in Strava doesn't count).
      if (apiSameDate && apiSameDate.wingoCount !== row.wingoCount && is2025(row.date)) {
        const idx = result.indexOf(apiSameDate);
        result.splice(idx, 1);
        result.push({ ...row, source: 'Log' });
      } else if (!apiSameDate) {
        result.push({ ...row, source: 'Log' });
      }
      // else: same date, different count, not 2025 → keep API row, don't add Log
    }
    return sortRowsNewestFirst(result);
  };

  const fetchData = async () => {
    try {
      setLoading(true);

      // Fetch both: API (Strava mining) and Transaction Log (Mining-only). Challenge = MINING ONLY.
      const [apiData, logData] = await Promise.all([
        fetchApiMining(),
        fetchLogMiningOnly(),
      ]);

      const apiRows = apiData?.rows ?? [];
      const logRows = logData?.rows ?? [];
      const merged = mergeMiningRows(apiRows, logRows);

      // If the user's first-ever entry comes via Strava, the first Wingo doesn't count.
      // We adjust only the challenge-page display (log remains accurate).
      const adjustFirstStravaRow = (rows: MiningRow[]): MiningRow[] => {
        const parsed = rows
          .map((r) => ({ r, t: parseRowDate(r.date) }))
          .filter((x) => Number.isFinite(x.t) && x.t > 0);
        if (parsed.length === 0) return rows;

        const minDate = Math.min(...parsed.map((x) => x.t));
        const earliestStravaIndex = rows.findIndex(
          (row) => row.source === 'Strava' && parseRowDate(row.date) === minDate
        );

        if (earliestStravaIndex < 0) return rows;

        return rows.map((row, idx) => {
          if (idx !== earliestStravaIndex) return row;
          return { ...row, wingoCount: Math.max(0, row.wingoCount - 1) };
        });
      };

      const adjusted = adjustFirstStravaRow(merged);

      if (logData?.displayName) setDisplayUsername(logData.displayName);
      else if (apiData?.displayName) setDisplayUsername(apiData.displayName);
      else if (username) setDisplayUsername(username);

      if (adjusted.length > 0) {
        setData(adjusted);
      } else if (import.meta.env?.DEV) {
        setData(getDevFallbackData());
      } else {
        setData([]);
      }
    } catch (error) {
      console.error('Error fetching challenge data:', error);
      if (import.meta.env?.DEV) {
        setData(getDevFallbackData());
        if (username) setDisplayUsername(username);
      } else {
        setData([]);
      }
    } finally {
      setLoading(false);
    }
  };

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white flex items-center justify-center">
        <div className="max-w-md w-full mx-auto px-4">
          <form onSubmit={handlePasswordSubmit} className="bg-white rounded-lg shadow-md p-8">
            <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
              Password
            </label>
            <input
              type="password"
              id="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#E6C200] focus:border-transparent"
              placeholder="Enter password"
              autoFocus
            />
            <button
              type="submit"
              className="mt-4 w-full bg-[#E6C200] text-white py-2 px-4 rounded-md hover:bg-[#D4B000] focus:outline-none focus:ring-2 focus:ring-[#E6C200] focus:ring-offset-2"
            >
              Submit
            </button>
          </form>
        </div>
      </div>
    );
  }

  // Calculate stats
  const totalWingo = data.reduce((sum, row) => sum + row.wingoCount, 0);
  const uniqueDays = new Set(data.map(row => row.date)).size;
  const topWingoSession = data.length ? Math.max(...data.map((row) => row.wingoCount)) : 0;
  const goal = 1000;
  const progress = Math.min((totalWingo / goal) * 100, 100);

  const remaining = Math.max(0, goal - totalWingo);

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      <div className="max-w-5xl mx-auto px-4 py-8 sm:py-12">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-5xl font-bold text-gray-900 mb-3">
            {displayUsername || username || 'Athlete'}
          </h1>
          <p className="text-lg text-gray-600">
            {(displayUsername || username)
              ? `${displayUsername || username}'s Wingo Segment Tracker`
              : 'Wingo Segment Tracker (All-Time)'}
          </p>
        </div>

        {/* Goal Card */}
        <div className="bg-gradient-to-br from-[#E6C200] to-[#D4B000] rounded-xl shadow-lg p-8 mb-6 text-white">
          <div className="text-center">
            <div className="text-sm font-medium mb-1 opacity-90">Wingo Goal</div>
            <div className="text-4xl font-bold mb-4">1,000</div>
            <div className="text-lg opacity-90">
              {remaining > 0 ? (
                <span>{remaining.toLocaleString()} remaining</span>
              ) : (
                <span className="font-semibold">Goal achieved</span>
              )}
            </div>
          </div>
        </div>

        {/* Progress Bar */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 mb-6">
          <div className="mb-2">
            <div className="flex justify-between items-center mb-3">
              <span className="text-base font-semibold text-gray-900">Progress</span>
              <span className="text-lg font-bold text-gray-900">{totalWingo.toLocaleString()} / {goal.toLocaleString()}</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-6 overflow-hidden">
              <div
                className="bg-gradient-to-r from-[#E6C200] to-[#D4B000] h-6 rounded-full transition-all duration-500 flex items-center justify-end pr-2"
                style={{ width: `${progress}%` }}
              >
                {progress > 10 && (
                  <span className="text-xs font-semibold text-white">{progress.toFixed(1)}%</span>
                )}
              </div>
            </div>
            {progress < 10 && (
              <div className="text-right mt-1">
                <span className="text-xs font-semibold text-gray-600">{progress.toFixed(1)}%</span>
              </div>
            )}
          </div>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <div className="text-sm text-gray-500 mb-2">Total Wingos</div>
            <div className="text-3xl font-bold text-gray-900">{totalWingo.toLocaleString()}</div>
          </div>
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <div className="text-sm text-gray-500 mb-2">Average per Session</div>
            <div className="text-3xl font-bold text-gray-900">
              {uniqueDays > 0 ? (totalWingo / uniqueDays).toFixed(1) : '0'}
            </div>
          </div>
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <div className="text-sm text-gray-500 mb-2">Top Wingo Session</div>
            <div className="text-3xl font-bold text-gray-900">{topWingoSession.toLocaleString()}</div>
          </div>
        </div>

        {/* Details (Collapsed by Default) */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          <button
            onClick={() => setShowDetails(!showDetails)}
            className="w-full px-6 py-4 text-left flex justify-between items-center hover:bg-gray-50 transition-colors"
          >
            <span className="font-semibold text-gray-900">{(displayUsername || username) ? `${displayUsername || username}'s Wingo Segment Tracker` : 'Wingo Segment Tracker'}</span>
            <span className="text-gray-500 text-xl">{showDetails ? '−' : '+'}</span>
          </button>
          
          {showDetails && (
            <div className="border-t border-gray-100">
              {loading ? (
                <div className="p-6 text-center text-gray-500">Loading...</div>
              ) : data.length === 0 ? (
                <div className="p-6 text-center text-gray-500">
                  No data available
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Date
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Wingos
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Strava Activity
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Source
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {data.map((row, index) => (
                        <tr key={index} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {row.date}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                            {row.wingoCount.toLocaleString()}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm">
                            {row.activityLink ? (
                              <a
                                href={row.activityLink}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-[#FC5200] font-semibold underline hover:opacity-90"
                              >
                                View
                              </a>
                            ) : (
                              <span className="text-gray-400">—</span>
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-xs text-gray-500">
                            {row.source}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot className="bg-gray-50">
                      <tr>
                        <td className="px-6 py-4 text-sm font-semibold text-gray-900">
                          Total
                        </td>
                        <td className="px-6 py-4 text-sm font-semibold text-gray-900">
                          {totalWingo.toLocaleString()}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-500">
                          —
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-500">
                          —
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Challenge;
